import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { api } from '../utils/api';
import { playBeep } from '../utils/beep';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isLoading, setIsLoading] = useState(true);
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const authBaseUrl = apiBaseUrl.replace(/\/api\/?$/, '');
  const queryClient = useQueryClient();

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await api.get('/auth/me');
          setUser(response.data.user);
        } catch (error) {
          console.error('Auth check failed:', error);
          logout();
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, [token]);

  useEffect(() => {
    if (!token) {
      return undefined;
    }

    const base = process.env.REACT_APP_WS_URL || authBaseUrl;
    const wsUrl = `${base.replace(/^http/, 'ws')}/ws?token=${token}`;
    let socket;
    let reconnectTimer;
    let isActive = true;

    const handleMessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'events_updated') {
          queryClient.invalidateQueries('upcomingEvents');
          queryClient.invalidateQueries('events');
          queryClient.invalidateQueries('calendarStatus');
          queryClient.invalidateQueries('oauthAccounts');
        }
        if (data.type === 'tasks_updated') {
          queryClient.invalidateQueries('tasks');
        }
        if (data.type === 'bookmarks_updated') {
          queryClient.invalidateQueries('bookmarks');
        }
        if (data.type === 'tasks_sync_finished') {
          queryClient.invalidateQueries('tasks');
          if (data.source === 'manual') {
            if (data.success) {
              toast.success(data.hasChanges ? 'Tasks synced' : 'Task sync completed');
            } else {
              toast.error(data.message || 'Task sync failed');
            }
          }
        }
        if (data.type === 'notes_updated') {
          queryClient.invalidateQueries('notes');
          queryClient.refetchQueries('notes');
        }
        if (data.type === 'note_reaction') {
          queryClient.setQueryData('notes', (prev = []) => {
            if (!Array.isArray(prev)) {
              return prev;
            }
            return prev.map((note) => (
              note.id === data.noteId
                ? {
                    ...note,
                    likesCount: data.likesCount,
                    dislikesCount: data.dislikesCount
                  }
                : note
            ));
          });
        }
        if (data.type === 'note_comment') {
          queryClient.setQueryData('notes', (prev = []) => {
            if (!Array.isArray(prev)) {
              return prev;
            }
            return prev.map((note) => (
              note.id === data.noteId
                ? {
                    ...note,
                    commentsCount: data.commentsCount
                  }
                : note
            ));
          });
          queryClient.setQueryData(['noteComments', data.noteId], (prev = []) => {
            if (!Array.isArray(prev)) {
              return prev;
            }
            const exists = prev.some((comment) => comment.id === data.comment?.id);
            if (exists || !data.comment) {
              return prev;
            }
            return [data.comment, ...prev];
          });
        }
        if (data.type === 'chat_new_guest') {
          if (data.chat && data.chatId) {
            queryClient.setQueryData('chats', (prev = []) => {
              if (!Array.isArray(prev)) {
                return prev;
              }
              const exists = prev.some((chat) => chat.publicId === data.chatId);
              if (!exists) {
                return [data.chat, ...prev];
              }
              return prev.map((chat) => (
                chat.publicId === data.chatId
                  ? {
                      ...chat,
                      newGuestCount: data.newGuestCount ?? chat.newGuestCount,
                      lastMessagePreview: data.chat.lastMessagePreview ?? chat.lastMessagePreview,
                      lastMessageAt: data.chat.lastMessageAt ?? chat.lastMessageAt
                    }
                  : chat
              ));
            });
          }
          queryClient.invalidateQueries('chats');
          if (data.chatId) {
            queryClient.invalidateQueries(['chat', data.chatId]);
            queryClient.invalidateQueries(['chatGuests', data.chatId]);
          }
        }
        if (data.type === 'chat_message') {
          if (data.senderType === 'guest') {
            playBeep();
          }
          if (data.chatId) {
            queryClient.setQueryData('chats', (prev = []) => {
              if (!Array.isArray(prev)) {
                return prev;
              }
              return prev.map((chat) => {
                if (chat.publicId !== data.chatId) {
                  return chat;
                }
                return {
                  ...chat,
                  lastMessagePreview: data.lastMessagePreview || chat.lastMessagePreview,
                  lastMessageAt: data.lastMessageAt || chat.lastMessageAt
                };
              });
            });
            queryClient.invalidateQueries(['chat', data.chatId]);
            queryClient.invalidateQueries(['chatGuests', data.chatId]);
          }

          if (data.chatId && data.guestId) {
            const messageKey = ['chatMessages', data.chatId, data.guestId];
            if (data.message) {
              queryClient.setQueryData(messageKey, (prev = []) => {
                if (!Array.isArray(prev)) {
                  return prev;
                }
                const exists = prev.some((msg) => msg.id === data.message.id);
                if (exists) {
                  return prev;
                }
                return [...prev, data.message];
              });
            }
            queryClient.refetchQueries(messageKey);
            queryClient.setQueryData(['chatGuests', data.chatId], (prev = []) => {
              if (!Array.isArray(prev)) {
                return prev;
              }
              return prev.map((guest) => {
                if (guest.id !== data.guestId) {
                  return guest;
                }
                return {
                  ...guest,
                  lastMessagePreview: data.lastMessagePreview || guest.lastMessagePreview,
                  lastMessageAt: data.lastMessageAt || guest.lastMessageAt
                };
              });
            });
          }
        }
      } catch (error) {
        console.warn('Failed to parse websocket message', error);
      }
    };

    const connect = () => {
      socket = new WebSocket(wsUrl);
      socket.onmessage = handleMessage;
      socket.onclose = () => {
        if (!isActive) {
          return;
        }
        reconnectTimer = setTimeout(connect, 2000);
      };
      socket.onerror = () => {
        socket.close();
      };
    };

    connect();

    return () => {
      isActive = false;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      if (socket) {
        socket.close();
      }
    };
  }, [token, authBaseUrl, queryClient]);

  // Login function
  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const { token: newToken, user: userData } = response.data;
      
      setToken(newToken);
      setUser(userData);
      localStorage.setItem('token', newToken);
      
      toast.success('Login successful!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      const { token: newToken, user: userInfo } = response.data;
      
      setToken(newToken);
      setUser(userInfo);
      localStorage.setItem('token', newToken);
      
      toast.success('Registration successful!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Logout function
  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    toast.success('Logged out successfully');
  };

  // Update user data
  const updateUser = (userData) => {
    setUser(userData);
  };

  // Link OAuth provider to the logged-in user
  const linkProvider = (provider, capability = 'calendar') => {
    if (!token) {
      toast.error('Please sign in before linking accounts');
      return;
    }
    const normalizedCapability = capability === 'tasks' ? 'tasks' : 'calendar';
    const url = `${authBaseUrl}/api/auth/${provider}/${normalizedCapability}?token=${encodeURIComponent(token)}`;
    window.location.href = url;
  };

  const value = {
    user,
    token,
    isLoading,
    login,
    register,
    logout,
    updateUser,
    linkProvider,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};


